import React from 'react';

import PropTypes from 'prop-types';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';

import { StylableComponent, stylePropType } from '@adsk/alloy-react-helpers';
import { DragIcon } from '@adsk/alloy-react-icon';

import ColumnOption from './ColumnOption';
import { isColumnDisabled } from './helpers';
import ReorderableColumnOption from './ReorderableColumnOption';
import { StrictModeDroppable } from './StrictModeDroppable';
import { TableConfigurationMenuColumnInstance } from './types';

export type TableConfigurationColumnsProps<D extends Record<string, unknown>> =
  StylableComponent<HTMLDivElement> & {
    handleOnDragEnd: (result: DropResult) => void;
    handleToggleColumn: (
      column: TableConfigurationMenuColumnInstance<D>
    ) => void;
    validColumns: TableConfigurationMenuColumnInstance<D>[];
    isColumnReorderable?: boolean;
  };

const TableConfigurationColumns = <D extends Record<string, unknown>>({
  handleOnDragEnd,
  handleToggleColumn,
  validColumns,
  isColumnReorderable,
  style,
  className,
}: TableConfigurationColumnsProps<D>) => (
  <DragDropContext onDragEnd={handleOnDragEnd}>
    <StrictModeDroppable droppableId="columns">
      {(provided) => (
        <div
          className={`columns ${className}`}
          css={[
            {
              maxHeight: 440,
              overflowY: 'scroll',
            },
            style,
          ]}
          {...provided.droppableProps}
          ref={provided.innerRef}
        >
          {validColumns.map((column, index) => {
            if (!isColumnReorderable) {
              return (
                <ColumnOption<D>
                  key={column.id}
                  column={column}
                  handleToggleColumn={() => handleToggleColumn(column)}
                />
              );
            }

            if (isColumnDisabled(column)) {
              return (
                <ColumnOption<D>
                  key={column.id}
                  column={column}
                  handleToggleColumn={() => handleToggleColumn(column)}
                >
                  <DragIcon style={{ marginRight: 8, cursor: 'not-allowed' }} />
                </ColumnOption>
              );
            }

            return (
              <ReorderableColumnOption<D>
                key={column.id}
                id={column.id}
                index={index}
                column={column}
                handleToggleColumn={() => handleToggleColumn(column)}
              />
            );
          })}
          {provided.placeholder}
        </div>
      )}
    </StrictModeDroppable>
  </DragDropContext>
);

TableConfigurationColumns.propTypes = {
  handleOnDragEnd: PropTypes.func.isRequired,
  handleToggleColumn: PropTypes.func.isRequired,
  validColumns: PropTypes.array.isRequired,
  isColumnReorderable: PropTypes.bool,
  className: PropTypes.string,
  style: stylePropType,
};

export default TableConfigurationColumns;
